<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div class="flex items-center gap-4">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2 px-3 text-gray-500 bg-transparent border-none rounded-lg text-sm cursor-pointer transition-colors hover:bg-gray-100"
                                                @click="goBack"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                </svg>
                                                Back to Regions
                                        </button>
                                </div>
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Region</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Update region properties</p>
                                </div>
                                <button
                                        type="button"
                                        :disabled="isSaving || !hasChanges"
                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                        @click="saveRegion"
                                >
                                        <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                </button>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading region...</span>
                        </div>

                        <!-- Edit Form -->
                        <div v-else class="max-w-3xl mx-auto">
                                <!-- Properties Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
                                        <!-- Card Header -->
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Region Properties</h2>
                                        </div>
                                        <!-- Card Body -->
                                        <div class="p-6">
                                                <div class="grid gap-5">
                                                        <!-- Name -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Name *</label>
                                                                <input
                                                                        v-model="region.name"
                                                                        type="text"
                                                                        placeholder="Enter region name"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                        </div>
                                                        <!-- Locale -->
                                                        <div class="grid grid-cols-[10rem_1fr] items-center gap-4">
                                                                <label class="text-sm font-medium text-gray-700">Default Locale</label>
                                                                <input
                                                                        v-model="region.locale"
                                                                        type="text"
                                                                        placeholder="e.g., en_US"
                                                                        class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7"
                                                                >
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Logos Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <!-- Card Header -->
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Logos</h2>
                                        </div>
                                        <!-- Card Body -->
                                        <div class="p-6">
                                                <div class="grid grid-cols-2 gap-6">
                                                        <!-- Desktop Logo -->
                                                        <div>
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Desktop Logo</label>
                                                                <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                                                        <img
                                                                                v-if="region.logo_desktop"
                                                                                :src="region.logo_desktop"
                                                                                alt="Desktop Logo"
                                                                                class="max-h-24 max-w-full object-contain mb-3"
                                                                        >
                                                                        <div v-else class="text-gray-400 text-sm mb-3">No logo uploaded</div>
                                                                        <div class="flex justify-center gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="py-1.5 px-3 bg-orange-7 text-white border-none rounded-md text-xs font-medium cursor-pointer"
                                                                                        @click="uploadDesktopLogo"
                                                                                >
                                                                                        Upload
                                                                                </button>
                                                                                <button
                                                                                        v-if="region.logo_desktop"
                                                                                        type="button"
                                                                                        class="py-1.5 px-3 bg-red-100 text-red-600 border-none rounded-md text-xs font-medium cursor-pointer"
                                                                                        @click="removeDesktopLogo"
                                                                                >
                                                                                        Remove
                                                                                </button>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                        <!-- Mobile Logo -->
                                                        <div>
                                                                <label class="block text-sm font-medium text-gray-700 mb-2">Mobile Logo</label>
                                                                <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                                                        <img
                                                                                v-if="region.logo_mobile"
                                                                                :src="region.logo_mobile"
                                                                                alt="Mobile Logo"
                                                                                class="max-h-24 max-w-full object-contain mb-3"
                                                                        >
                                                                        <div v-else class="text-gray-400 text-sm mb-3">No logo uploaded</div>
                                                                        <div class="flex justify-center gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="py-1.5 px-3 bg-orange-7 text-white border-none rounded-md text-xs font-medium cursor-pointer"
                                                                                        @click="uploadMobileLogo"
                                                                                >
                                                                                        Upload
                                                                                </button>
                                                                                <button
                                                                                        v-if="region.logo_mobile"
                                                                                        type="button"
                                                                                        class="py-1.5 px-3 bg-red-100 text-red-600 border-none rounded-md text-xs font-medium cursor-pointer"
                                                                                        @click="removeMobileLogo"
                                                                                >
                                                                                        Remove
                                                                                </button>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
                                        @click="hideToast"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, getCurrentInstance, Ref, ComputedRef } from 'vue';
import { useRouter, useRoute, Router, RouteLocationNormalizedLoaded } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import RegionService from 'services/RegionService';
import type { RegionModel } from '@sosocio/creator/interfaces';

interface ExtendedRegionModel extends Partial<RegionModel> {
        logo_desktop?: string | null;
        logo_mobile?: string | null;
}

interface FilestackOptions {
        accept: string;
        maxFiles: number;
        onUploadDone: (files: FilestackUploadResponse) => void;
}

interface FilestackUploadResponse {
        filesUploaded?: Array<{
                url: string;
        }>;
}

interface FilestackClient {
        picker: (options: FilestackOptions) => { open: () => void };
}

export default defineComponent({
        name: 'EditRegionPage',
        setup() {
                const router: Router = useRouter();
                const route: RouteLocationNormalizedLoaded = useRoute();
                const auth0 = useAuth0();
                const instance = getCurrentInstance();
                const filestackClient: FilestackClient | undefined = instance?.appContext.config.globalProperties.$client;

                const region: Ref<ExtendedRegionModel> = ref({
                        name: '',
                        locale: '',
                        logo_desktop: null,
                        logo_mobile: null,
                });
                const originalRegion: Ref<ExtendedRegionModel | null> = ref(null);
                const isLoading: Ref<boolean> = ref(true);
                const isSaving: Ref<boolean> = ref(false);

                const toastMessage: Ref<string> = ref('');
                const toastVariant: Ref<string> = ref('success');
                let toastTimeout: ReturnType<typeof setTimeout> | null = null;

                const loggedIn: ComputedRef<boolean> = computed(() => auth0.isAuthenticated.value);

                const hasChanges: ComputedRef<boolean> = computed(() => {
                        if (!originalRegion.value) return false;
                        return (
                                region.value.name !== originalRegion.value.name ||
                                region.value.locale !== originalRegion.value.locale ||
                                region.value.logo_desktop !== originalRegion.value.logo_desktop ||
                                region.value.logo_mobile !== originalRegion.value.logo_mobile
                        );
                });

                const showToast = (message: string, variant: string = 'success'): void => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 4000);
                };

                const hideToast = (): void => {
                        toastMessage.value = '';
                        if (toastTimeout) clearTimeout(toastTimeout);
                };

                const goBack = (): void => {
                        router.push('/region');
                };

                const loadData = async (): Promise<void> => {
                        if (!loggedIn.value) return;

                        isLoading.value = true;
                        try {
                                const regionId = Number(route.params.id);
                                const data = await RegionService.get(regionId);
                                region.value = { ...data };
                                originalRegion.value = { ...data };
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to load region';
                                showToast(message, 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };

                const saveRegion = async (): Promise<void> => {
                        if (!hasChanges.value) return;

                        isSaving.value = true;
                        try {
                                await RegionService.update(region.value.id!, { ...region.value });
                                originalRegion.value = { ...region.value };
                                showToast('Region saved successfully', 'success');
                        } catch (error: unknown) {
                                const message = error instanceof Error ? error.message : 'Failed to save region';
                                showToast(message, 'error');
                        } finally {
                                isSaving.value = false;
                        }
                };

                const uploadDesktopLogo = (): void => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        const options: FilestackOptions = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (files: FilestackUploadResponse) => {
                                        if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                region.value.logo_desktop = files.filesUploaded[0].url;
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const uploadMobileLogo = (): void => {
                        if (!filestackClient) {
                                showToast('File upload is not available', 'error');
                                return;
                        }
                        const options: FilestackOptions = {
                                accept: 'image/*',
                                maxFiles: 1,
                                onUploadDone: (files: FilestackUploadResponse) => {
                                        if (files.filesUploaded && files.filesUploaded.length > 0) {
                                                region.value.logo_mobile = files.filesUploaded[0].url;
                                        }
                                },
                        };
                        filestackClient.picker(options).open();
                };

                const removeDesktopLogo = (): void => {
                        region.value.logo_desktop = '';
                };

                const removeMobileLogo = (): void => {
                        region.value.logo_mobile = '';
                };

                watch(loggedIn, async (isLoggedIn: boolean) => {
                        if (isLoggedIn) {
                                await loadData();
                        }
                }, { immediate: true });

                return {
                        region,
                        isLoading,
                        isSaving,
                        hasChanges,
                        toastMessage,
                        toastVariant,
                        goBack,
                        saveRegion,
                        uploadDesktopLogo,
                        uploadMobileLogo,
                        removeDesktopLogo,
                        removeMobileLogo,
                        hideToast,
                };
        },
});
</script>
